import { useState } from 'react';
import { useAdmin } from '../../contexts/AdminContext';

/**
 * AdminGate — route-level wrapper that blocks non-admin access.
 * Renders children when isAdmin === true.
 * Otherwise shows a centered lock card with two sign-in options:
 *   1. Google (for staff with @ixoragroup.com / @loocafe.com accounts)
 *   2. IxoraOne Employee ID + password (for employees granted feedbackDashboardAccess)
 */
export default function AdminGate({ children }) {
  const { isAdmin, signIn, signInWithEmployeeId, signingIn } = useAdmin();
  const [tab, setTab] = useState('employee'); // 'google' | 'employee'
  const [employeeId, setEmployeeId] = useState('');
  const [password, setPassword] = useState('');
  const [empError, setEmpError] = useState('');

  if (isAdmin) return children;

  const handleEmployeeSignIn = async (e) => {
    e.preventDefault();
    setEmpError('');
    if (!employeeId.trim() || !password) {
      setEmpError('Enter your Employee ID and password.');
      return;
    }
    try {
      await signInWithEmployeeId(employeeId.trim(), password);
    } catch (err) {
      const msg = err?.message || 'Sign-in failed. Check your ID and password.';
      // Translate Firebase auth codes to friendly messages.
      if (err?.code === 'auth/wrong-password' || err?.code === 'auth/invalid-credential') {
        setEmpError('Incorrect password.');
      } else if (err?.code === 'auth/user-not-found') {
        setEmpError('Employee ID not found.');
      } else if (err?.code === 'auth/too-many-requests') {
        setEmpError('Too many attempts. Try again later.');
      } else {
        setEmpError(msg);
      }
    }
  };

  const tabStyle = (active) => ({
    flex: 1,
    padding: '9px 0',
    border: 'none',
    borderBottom: active ? '2px solid var(--c-brand-600, #EA580C)' : '2px solid transparent',
    background: 'transparent',
    fontFamily: 'var(--font-mono, "Geist Mono", monospace)',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.12em',
    color: active ? 'var(--c-brand-600, #EA580C)' : 'var(--c-ink-3)',
    cursor: 'pointer',
    transition: 'color 120ms, border-color 120ms',
  });

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--c-line-hard, #d1d5db)',
    borderRadius: 'var(--r-md, 6px)',
    fontFamily: 'var(--font-mono, "Geist Mono", monospace)',
    fontSize: 13,
    color: 'var(--c-ink)',
    background: 'var(--c-bg)',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const btnPrimary = {
    width: '100%',
    padding: '11px 0',
    border: 'none',
    borderRadius: 'var(--r-md, 6px)',
    background: signingIn ? 'var(--c-line-2, #e5e7eb)' : 'var(--c-brand-600, #EA580C)',
    color: '#fff',
    fontFamily: 'var(--font-mono, "Geist Mono", monospace)',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.08em',
    cursor: signingIn ? 'wait' : 'pointer',
    transition: 'background 150ms',
    marginTop: 4,
  };

  return (
    <div style={{
      minHeight: '70vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--sp-6)',
      background: 'var(--c-bg)',
    }}>
      <div style={{
        background: 'var(--c-surface)',
        border: '1px solid var(--c-line-hard, var(--c-line-2))',
        borderRadius: 'var(--r-lg)',
        padding: 'var(--sp-10) var(--sp-8)',
        maxWidth: 400,
        width: '100%',
        boxShadow: 'var(--shadow-2)',
      }}>
        {/* Lock icon */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--sp-5)' }}>
          <svg
            aria-hidden="true"
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--c-ink-3)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <p style={{
          fontFamily: 'var(--font-mono, "Geist Mono", monospace)',
          fontSize: 10,
          letterSpacing: '0.12em',
          color: 'var(--c-brand-600, #EA580C)',
          textTransform: 'uppercase',
          textAlign: 'center',
          marginBottom: 'var(--sp-2)',
        }}>
          RESTRICTED
        </p>
        <h2 style={{
          fontFamily: 'var(--font-mono, "Geist Mono", monospace)',
          fontSize: 'var(--fs-lg)',
          fontWeight: 600,
          color: 'var(--c-ink)',
          letterSpacing: '-0.01em',
          textAlign: 'center',
          marginBottom: 'var(--sp-7)',
        }}>
          ADMIN ACCESS REQUIRED
        </h2>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--c-line-2, #e5e7eb)', marginBottom: 'var(--sp-6)' }}>
          <button type="button" style={tabStyle(tab === 'employee')} onClick={() => setTab('employee')}>
            EMPLOYEE ID
          </button>
          <button type="button" style={tabStyle(tab === 'google')} onClick={() => setTab('google')}>
            GOOGLE
          </button>
        </div>

        {/* Employee ID tab */}
        {tab === 'employee' && (
          <form onSubmit={handleEmployeeSignIn} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-mono, "Geist Mono", monospace)',
                fontSize: 10,
                letterSpacing: '0.10em',
                color: 'var(--c-ink-3)',
                marginBottom: 5,
              }}>
                EMPLOYEE ID
              </label>
              <input
                type="text"
                placeholder="ICS000001"
                value={employeeId}
                onChange={(e) => { setEmployeeId(e.target.value.toUpperCase()); setEmpError(''); }}
                disabled={signingIn}
                autoComplete="username"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-mono, "Geist Mono", monospace)',
                fontSize: 10,
                letterSpacing: '0.10em',
                color: 'var(--c-ink-3)',
                marginBottom: 5,
              }}>
                PASSWORD
              </label>
              <input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setEmpError(''); }}
                disabled={signingIn}
                autoComplete="current-password"
                style={inputStyle}
              />
            </div>
            {empError && (
              <p style={{
                fontFamily: 'var(--font-mono, "Geist Mono", monospace)',
                fontSize: 11,
                color: 'var(--c-danger, #dc2626)',
                margin: 0,
              }}>
                {empError}
              </p>
            )}
            <button type="submit" disabled={signingIn} style={btnPrimary}>
              {signingIn ? 'SIGNING IN…' : 'SIGN IN'}
            </button>
          </form>
        )}

        {/* Google tab */}
        {tab === 'google' && (
          <div style={{ textAlign: 'center' }}>
            <p style={{
              fontSize: 'var(--fs-sm)',
              color: 'var(--c-ink-3)',
              lineHeight: 1.6,
              marginBottom: 'var(--sp-6)',
            }}>
              Sign in with an authorised <strong>@ixoragroup.com</strong> or{' '}
              <strong>@loocafe.com</strong> Google account.
            </p>
            <button
              type="button"
              onClick={signIn}
              disabled={signingIn}
              style={btnPrimary}
            >
              {signingIn ? 'SIGNING IN…' : 'SIGN IN WITH GOOGLE'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
